<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useToast } from "primevue/usetoast";
import { useSocketStore } from "../../stores/socketStore";
import TimeTrackingService from "../../services/TimeTrackingService";
import { useTimeTrackingStore } from "../../stores/timeTrackingStore";
import { useAuthStore } from "../../stores/authStore";

const socketStore = useSocketStore();
const timeTrackingStore = useTimeTrackingStore();
const authStore = useAuthStore();

const visible = defineModel('visible');

const props = defineProps<{
  buildId: number;
  gensetNumber?: string;
}>();

const emit = defineEmits(['timeTrackingStarted', 'timeTrackingStopped', 'workersUpdated']);

const toast = useToast();

interface ActiveWorker {
  userId: number;
  firstName: string;
  lastName: string;
  startTime: Date;
  elapsedSeconds?: number;
}

const activeWorkers = ref<ActiveWorker[]>([]);
const loading = ref(false);
const pendingTimerOperation = ref<Promise<void> | null>(null);
const workerTimerInterval = ref<NodeJS.Timeout | null>(null);

// Check if the current user has an active time record for this build
const currentUserTimeRecord = computed(() => {
  return timeTrackingStore.activeTimeRecords.find(record => {
    if (record.type === 'Build' || record.recordDetails?.buildId) {
      const recordBuildId = record.recordDetails?.buildId;
      return (
        (Number(recordBuildId) === Number(props.buildId)) &&
        record.isStartedByCurrentUser
      );
    }
    return false;
  });
});

// Get elapsed seconds for the current user's record
const currentUserElapsedSeconds = computed(() => {
  if (currentUserTimeRecord.value) {
    return currentUserTimeRecord.value.elapsedSeconds || 0;
  }
  return 0;
});

// Check if the current user is currently tracking time on this build
const isCurrentUserTracking = computed(() => {
  return !!currentUserTimeRecord.value;
});

// Format seconds to HH:MM:SS
const formatElapsedTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Update elapsed times for active workers
const updateWorkerElapsedTimes = () => {
  const now = new Date();
  activeWorkers.value = activeWorkers.value.map(worker => {
    const startTime = new Date(worker.startTime);
    const elapsedSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
    return {
      ...worker,
      elapsedSeconds: elapsedSeconds >= 0 ? elapsedSeconds : 0
    };
  });
};

// Start worker timer
const startWorkerTimer = () => {
  stopWorkerTimer();
  workerTimerInterval.value = setInterval(updateWorkerElapsedTimes, 1000);
};

// Stop worker timer
const stopWorkerTimer = () => {
  if (workerTimerInterval.value) {
    clearInterval(workerTimerInterval.value);
    workerTimerInterval.value = null;
  }
};

// Fetch current build time data
const fetchBuildTimeData = async () => {
  try {
    loading.value = true;
    const response = await TimeTrackingService.getBuildCurrentTime(props.buildId);

    // Response is directly the data object with { active, activeWorkers, startTime?, id? }
    if (response?.activeWorkers) {
      activeWorkers.value = response.activeWorkers.map((worker: any) => ({
        userId: worker.userId,
        firstName: worker.firstName,
        lastName: worker.lastName,
        startTime: new Date(worker.startTime),
        elapsedSeconds: 0
      }));

      // Start the timer if there are active workers
      if (activeWorkers.value.length > 0) {
        updateWorkerElapsedTimes();
        startWorkerTimer();
      }
    }

    // Also refresh the store's active time records
    await timeTrackingStore.fetchActiveTimeRecords();
  } catch (error) {
    console.error('Error fetching build time data:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch time tracking data',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
};

// Start time tracking for current user
const startTime = async () => {
  try {
    if (pendingTimerOperation.value) {
      await pendingTimerOperation.value;
    }

    let resolveOperation: () => void;
    pendingTimerOperation.value = new Promise(resolve => {
      resolveOperation = resolve;
    });

    const response = await TimeTrackingService.startBuildTime(props.buildId);
    console.log('Start build time response:', response);

    if (response?.success === false && response?.error) {
      // Handle existing active time on another build
      if (response.error.code === 'ACTIVE_TIME_EXISTS') {
        toast.add({
          severity: 'warn',
          summary: 'Already Tracking Time',
          detail: response.error.message || 'You already have an active time record on another build',
          life: 5000
        });
      } else {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: response.error.message || 'Failed to start time',
          life: 3000
        });
      }
      resolveOperation!();
      pendingTimerOperation.value = null;
      return;
    }

    emit('timeTrackingStarted', props.buildId);

    toast.add({
      severity: 'success',
      summary: 'Started',
      detail: 'Time tracking started',
      life: 3000
    });

    // Refresh data
    const fetchPromise = Promise.all([
      fetchBuildTimeData(),
      timeTrackingStore.fetchActiveTimeRecords()
    ]);

    resolveOperation!();
    pendingTimerOperation.value = null;

    await fetchPromise;
  } catch (error: any) {
    pendingTimerOperation.value = null;
    console.error('Error starting time:', error);

    const errorMessage = error.response?.data?.error?.message || 'Failed to start time';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 3000
    });
  }
};

// Stop time tracking for current user
const stopTime = async () => {
  try {
    if (pendingTimerOperation.value) {
      await pendingTimerOperation.value;
    }

    let resolveOperation: () => void;
    pendingTimerOperation.value = new Promise(resolve => {
      resolveOperation = resolve;
    });

    const response = await TimeTrackingService.endBuildTime(props.buildId);
    console.log('End build time response:', response);

    emit('timeTrackingStopped', props.buildId);

    toast.add({
      severity: 'success',
      summary: 'Stopped',
      detail: 'Time tracking stopped',
      life: 3000
    });

    // Refresh data
    const fetchPromise = Promise.all([
      fetchBuildTimeData(),
      timeTrackingStore.fetchActiveTimeRecords()
    ]);

    resolveOperation!();
    pendingTimerOperation.value = null;

    await fetchPromise;
  } catch (error: any) {
    pendingTimerOperation.value = null;
    console.error('Error stopping time:', error);

    const errorMessage = error.response?.data?.error?.message || 'Failed to stop time';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 3000
    });
  }
};

// Socket event handler for build time updates
const handleBuildTimeUpdate = (data: any) => {
  console.log('Received buildTimeTracking:updated event:', data);
  if (data?.buildId === props.buildId) {
    void fetchBuildTimeData();
  }
};

onMounted(async () => {
  await fetchBuildTimeData();
  timeTrackingStore.startTimer();

  // Listen for socket updates
  if (socketStore.socket) {
    socketStore.socket.on('buildTimeTracking:updated', handleBuildTimeUpdate);
    socketStore.socket.emit('joinBuild', props.buildId);
  }
});

onBeforeUnmount(() => {
  stopWorkerTimer();

  if (socketStore.socket) {
    socketStore.socket.off('buildTimeTracking:updated', handleBuildTimeUpdate);
    socketStore.socket.emit('leaveBuild', props.buildId);
  }
});

// Watch for visibility changes to refresh data
watch(visible, async (newValue) => {
  if (newValue) {
    await fetchBuildTimeData();
  }
});
</script>

<template>
  <div v-if="visible">
    <div
      id="slideover-build-time"
      class="bg-white dark:bg-black z-50 bg-surface-0 h-full absolute top-0 right-0 shadow w-96"
      style="z-index: 101"
    >
      <div class="flex flex-col h-full">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4 p-6 border-b border-surface">
          <div>
            <span class="text-surface-900 dark:text-surface-0 text-xl font-medium block">Build Time Tracking</span>
            <span
              v-if="gensetNumber"
              class="text-surface-500 text-sm"
            >Genset {{ gensetNumber }}</span>
          </div>
          <Button
            icon="pi pi-times"
            class="p-button-rounded p-button-text p-button-plain"
            @click="visible = false"
          />
        </div>

        <!-- Content -->
        <div class="flex-auto overflow-y-auto px-6">
          <!-- Loading state -->
          <div
            v-if="loading"
            class="flex justify-center py-8"
          >
            <i class="pi pi-spin pi-spinner text-2xl" />
          </div>

          <template v-else>
            <!-- Your Time Section -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold mb-3 text-surface-700 dark:text-surface-200">
                Your Time
              </h3>
              <div class="flex items-center gap-4 p-4 bg-surface-100 dark:bg-surface-800 rounded-lg">
                <span
                  v-if="isCurrentUserTracking"
                  class="rounded-full bg-green-500 text-white text-xl inline-flex items-center justify-center flex-shrink-0"
                  style="width:40px;height:40px"
                >
                  <i class="pi pi-clock" />
                </span>
                <span
                  v-else
                  class="rounded-full border-2 border-surface-300 bg-white text-surface-500 text-xl inline-flex items-center justify-center flex-shrink-0"
                  style="width:40px;height:40px"
                >
                  <i class="pi pi-clock" />
                </span>

                <div class="flex-grow">
                  <div
                    v-if="isCurrentUserTracking"
                    class="flex items-center gap-3"
                  >
                    <span class="text-2xl font-mono font-bold text-green-600">
                      {{ formatElapsedTime(currentUserElapsedSeconds) }}
                    </span>
                    <Button
                      label="Stop"
                      severity="danger"
                      size="small"
                      :loading="!!pendingTimerOperation"
                      @click="stopTime"
                    />
                  </div>
                  <div v-else>
                    <Button
                      label="Start Time"
                      severity="success"
                      :loading="!!pendingTimerOperation"
                      @click="startTime"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Active Workers Section -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold mb-3 text-surface-700 dark:text-surface-200">
                Active Workers ({{ activeWorkers.length }})
              </h3>

              <div
                v-if="activeWorkers.length === 0"
                class="text-surface-500 text-sm py-4 text-center"
              >
                No workers currently tracking time
              </div>

              <ul
                v-else
                class="list-none p-0 m-0 space-y-2"
              >
                <li
                  v-for="worker in activeWorkers"
                  :key="worker.userId"
                  class="flex items-center justify-between p-3 bg-surface-50 dark:bg-surface-700 rounded-lg"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="rounded-full bg-blue-500 text-white text-sm inline-flex items-center justify-center flex-shrink-0"
                      style="width:32px;height:32px"
                    >
                      {{ worker.firstName?.[0] }}{{ worker.lastName?.[0] }}
                    </span>
                    <span class="text-surface-700 dark:text-surface-200 font-medium">
                      {{ worker.firstName }} {{ worker.lastName }}
                    </span>
                  </div>
                  <span class="font-mono text-surface-600 dark:text-surface-300">
                    {{ formatElapsedTime(worker.elapsedSeconds || 0) }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
